import { filterAndMap } from '@rentguru/commons-utils';
import { setCacheItem } from '@up2rent/graphql';
import { LoadingScreen } from '@up2rent/ui';
import { AuthLayout, Skeleton } from '@up2rent/ui-new';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormType } from './AuthTypes';
import ImpersonationBanner from './components/Auth/ImpersonationBanner';
import { retryLazy } from './components/Routes/LazyErrorBoundary';
import RouteDestination from './components/Routes/RouteDestination';
import './css/App.css';
import { resetDatabase } from './hooks/Folderhook/FolderDatabaseContext';
import { useEmailChangeCallbackFeedback } from './hooks/useEmailChangeCallbackFeedback';
import { useUser, WorkspaceIssueKind } from './hooks/UserContext';
import { GOOGLE_CALLBACK_CACHE, MICROSOFT_CALLBACK_CACHE, PONTO_CALLBACK_CACHE } from './utils/cacheKeys';
import { getDisplayVersion } from './utils/envUtils';
// REN-4671 const ConfigureMandate = lazy(() => retryLazy(() => import('./components/Auth/ConfigureMandate')));
const StripeMandateActivated = lazy(() => retryLazy(() => import('./components/Auth/StripeMandateActivated')));
const AcceptInvitation = lazy(() => retryLazy(() => import('./components/Auth/AcceptInvitation')));
const ConfirmSignUp = lazy(() => retryLazy(() => import('./components/Auth/ConfirmSignUp')));
const CreateAccount = lazy(() => retryLazy(() => import('./components/Auth/CreateAccount')));
const ForgotPassword = lazy(() => retryLazy(() => import('./components/Auth/ForgotPassword')));
const Login = lazy(() => retryLazy(() => import('./components/Auth/Login')));
const PasswordMigrationBanner = lazy(() => retryLazy(() => import('./components/Auth/PasswordMigrationBanner')));
const ResetPassword = lazy(() => retryLazy(() => import('./components/Auth/ResetPassword')));
const Signup = lazy(() => retryLazy(() => import('./components/Auth/Signup')));

const SetInitialPassword = lazy(() => retryLazy(() => import('./components/Auth/SetInitialPassword')));
const AccessRevoked = lazy(() => retryLazy(() => import('./components/Auth/AccessRevoked')));
const WorkspaceIssueScreen = lazy(() => retryLazy(() => import('./components/Auth/WorkspaceIssueScreen')));
const AccountPendingDeletionScreen = lazy(() =>
  retryLazy(() => import('./components/Auth/AccountPendingDeletionScreen'))
);
const WorkspaceClosedScreen = lazy(() => retryLazy(() => import('./components/Auth/WorkspaceClosedScreen')));

const Routes = lazy(() => retryLazy(() => import('./components/Routes/Routes')));
const TenantRoutes = lazy(() => retryLazy(() => import('./components/Routes/TenantRoutes')));

const SIGN_IN_DETAILS_KEY = 'signInDetails';

interface AppProps {
  [key: string]: unknown;
}

const findLocalItems = (query: string) => {
  return Object.entries(localStorage).reduce(
    filterAndMap({
      filter: ([key]) => Boolean(key.match(query)) || (!query && typeof key === 'string'),
      map: ([key, val]) => ({ key, val }),
    }),
    []
  );
};

const App: React.FC<AppProps> = (props) => {
  const {
    accessRevoked,
    clientId,
    hasPassword,
    impersonatedBy,
    isFetchingUser,
    confirmSignUpChallenge,
    isTenant,
    userAttributes,
    workspaceIssue,
  } = useUser();
  useEmailChangeCallbackFeedback();

  useEffect(() => {
    const handleStorageChange = (change: StorageEvent) => {
      if (!change.key || !change.key.includes(SIGN_IN_DETAILS_KEY)) return;
      checkLocalStorageForSignInDetails();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { formatMessage } = useIntl();
  const [formType, updateFormType] = useState<FormType>(FormType.SIGN_IN);

  const checkLocalStorageForSignInDetails = () => {
    const signInDetails = findLocalItems(SIGN_IN_DETAILS_KEY);
    if (isEmpty(signInDetails)) {
      // Session state is handled reactively by Better Auth's useSession hook
      return;
    }

    const signInDetailsObject = JSON.parse(signInDetails[0].val);
    if (!signInDetailsObject) return;
    if (isNil(userAttributes?.email)) {
      window.location.reload();
    }

    if (!isNil(userAttributes?.email) && userAttributes?.email !== signInDetailsObject.loginId) {
      window.location.reload();
    }
  };

  useEffect(() => {
    const setCacheItems = async () => {
      await resetDatabase();
      const queryStrings = new URLSearchParams(window.location.search);
      const path = window.location.pathname;
      const code = queryStrings.get('code');
      const state = queryStrings.get('state');
      if (!code) {
        return;
      }
      if (path === RouteDestination.PONTO_CALLBACK && state) {
        await setCacheItem(PONTO_CALLBACK_CACHE, { pontoCode: code, pontoState: state });
        return;
      }
      if (path === RouteDestination.MICROSOFT_CALLBACK) {
        await setCacheItem(MICROSOFT_CALLBACK_CACHE, { code });
        return;
      }
      if (path === RouteDestination.GOOGLE_CALLBACK) {
        await setCacheItem(GOOGLE_CALLBACK_CACHE, { code });
        return;
      }
    };
    setCacheItems();
  }, []);

  // REN-4671: useEffect(() => {
  //   const handleSEPAConfigurationStep = async () => {
  //     if (formType !== FormType.LOADING) {
  //       return;
  //     }
  //     const sepaConfigStepCache: boolean | undefined = await getCacheItem(ACCOUNT_SEPA_CONFIG_CACHE);
  //     if (sepaConfigStepCache) {
  //       updateFormType(FormType.SEPA_CONFIGURATION);
  //     } else {
  //       updateFormType(FormType.SIGN_IN);
  //     }
  //   };
  //   handleSEPAConfigurationStep();
  // }, [formType]);

  if (accessRevoked) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AccessRevoked />
      </Suspense>
    );
  }

  if (window.location.pathname === RouteDestination.STRIPE_MANDATE_ACTIVATED) {
    return (
      <Suspense fallback={<Skeleton className="h-64 w-128" />}>
        <StripeMandateActivated />
      </Suspense>
    );
  }

  if (window.location.pathname.startsWith(`${RouteDestination.ACCEPT_INVITATION}/`)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AcceptInvitation />
      </Suspense>
    );
  }

  if (window.location.pathname === RouteDestination.RESET_PASSWORD) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <ResetPassword />
      </Suspense>
    );
  }

  if (isFetchingUser) {
    return <LoadingScreen />;
  }

  if (!isNil(workspaceIssue) && workspaceIssue.kind === WorkspaceIssueKind.ACCOUNT_PENDING_DELETION) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <AccountPendingDeletionScreen issue={workspaceIssue} />
      </Suspense>
    );
  }

  if (!isNil(workspaceIssue) && workspaceIssue.kind === WorkspaceIssueKind.WORKSPACE_CLOSED) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <WorkspaceClosedScreen issue={workspaceIssue} />
      </Suspense>
    );
  }

  if (!isNil(workspaceIssue)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <WorkspaceIssueScreen issue={workspaceIssue} />
      </Suspense>
    );
  }

  if (userAttributes && hasPassword === false && isNil(impersonatedBy)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <SetInitialPassword />
      </Suspense>
    );
  }

  // REN-4671 const showSepaConfig = clientId && formType === FormType.SEPA_CONFIGURATION;

  // REN-4671 if (!showSepaConfig && clientId) {
  if (clientId) {
    if (isTenant) {
      return (
        <Suspense fallback={<Skeleton className="h-6 w-full" />}>
          <ImpersonationBanner />
          <TenantRoutes {...props} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<Skeleton className="h-6 w-full" />}>
        <Routes {...props} />
      </Suspense>
    );
  }

  if (formType === FormType.LOADING) {
    return <LoadingScreen />;
  }

  const noUserAttributes = isNil(userAttributes);
  const noUserAttributesAndNoConfirmSignUpChallenge = noUserAttributes && !confirmSignUpChallenge;
  const showLogin = noUserAttributesAndNoConfirmSignUpChallenge && formType === FormType.SIGN_IN;
  const showForgotPassword = noUserAttributes && formType === FormType.FORGOT_PASSWORD;
  const showSignUp = noUserAttributesAndNoConfirmSignUpChallenge && formType === FormType.SIGN_UP;
  const showConfirmSignUp = noUserAttributes && confirmSignUpChallenge;
  const showCreateAccount = userAttributes && !clientId && isNil(impersonatedBy);

  return (
    <AuthLayout
      banner={
        <Suspense fallback={null}>
          {showLogin && <PasswordMigrationBanner onResetPassword={() => updateFormType(FormType.FORGOT_PASSWORD)} />}
        </Suspense>
      }
      footer={formatMessage({ id: 'menu.version' }, { version: getDisplayVersion() })}
    >
      <Suspense fallback={<Skeleton className="h-64 w-128 max-w-[90vw]" />}>
        {showLogin && <Login key={'loginPage'} updateFormType={updateFormType} />}
        {showForgotPassword && <ForgotPassword key={'forgotPasswordPage'} updateFormType={updateFormType} />}
        {showSignUp && <Signup key={'signupPage'} updateFormType={updateFormType} />}
        {showConfirmSignUp && <ConfirmSignUp key={'confirmSignUpPage'} updateFormType={updateFormType} />}
        {showCreateAccount && <CreateAccount key={'createAccountPage'} updateFormType={updateFormType} />}
        {/* REN-4671 {showSepaConfig && <ConfigureMandate key={'configureMandatePage'} />} */}
      </Suspense>
    </AuthLayout>
  );
};

export default App;
